(() => {
  if (customElements.get('ugc-meta-swiper')) return;

class UgcMetaSwiper extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this._visibilityObserver = null;
    this._muteHandler = this._onMuteClick.bind(this);
    this._controlsHideDelay = 500;
    this._shouldAutoplay = false;
    this._autoplayDelay = 3500;
    this._isVisible = false;
  }

  connectedCallback() {
    this.initialize();
    this.handleSectionEvents = this.handleSectionEvents.bind(this);
    document.addEventListener('shopify:section:load', this.handleSectionEvents);
    document.addEventListener('shopify:section:reorder', this.handleSectionEvents);
  }

  disconnectedCallback() {
    this.destroy();
    document.removeEventListener('shopify:section:load', this.handleSectionEvents);
    document.removeEventListener('shopify:section:reorder', this.handleSectionEvents);
  }

  handleSectionEvents(event) {
    if (!event?.target?.contains(this) && !this.contains(event?.target)) return;
    this.initialize(true);
  }

  initialize(force = false) {
    if (typeof Swiper === 'undefined') return;

    const slider = this.querySelector('.ugc-meta-swiper__slider');
    const slideCount = this.querySelectorAll('.swiper-slide').length;
    if (!slider || slideCount === 0) return;

    if (this.swiper && !force) return;

    this.destroy();

    const showArrows = this.dataset.showArrows === 'true';
    const hasEnoughSlides = slideCount > 1;
    const shouldLoop = this.dataset.loop === 'true' && hasEnoughSlides;
    this._shouldAutoplay =
      this.dataset.autoplay === 'true' && hasEnoughSlides;
    this._autoplayDelay = Number(this.dataset.autoplayDelay) || 3500;
    const itemsPerView = Number(this.dataset.itemsPerView) || 3;
    const itemsPerViewMobile = Number(this.dataset.itemsPerViewMobile) || 1.2;
    const spaceBetween = Number(this.dataset.spaceBetween) || 20;
    const centeredSlides = this.dataset.centeredSlides !== 'false';

    this.swiper = new Swiper(slider, {
      slidesPerView: itemsPerViewMobile,
      spaceBetween,
      centeredSlides,
      watchSlidesProgress: true,
      loop: shouldLoop,
      keyboard: {
        enabled: this.dataset.keyboard === 'true',
        onlyInViewport: true
      },
      autoplay: this._shouldAutoplay
        ? {
            delay: this._autoplayDelay,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
            waitForTransition: true
          }
        : false,
      navigation: showArrows
        ? {
            nextEl: this.querySelector('.ugc-meta-swiper__arrow--next'),
            prevEl: this.querySelector('.ugc-meta-swiper__arrow--prev')
          }
        : undefined,
      breakpoints: {
        750: {
          slidesPerView: itemsPerView,
          spaceBetween
        }
      },
      on: {
        init: () => {
          this._bindMuteButtons();
          this._bindPlayControls();
          requestAnimationFrame(() => {
            if (this._isVisible) this._onActiveSlideChange();
          });
        },
        slideChange: () => {
          if (this._isVisible) this._hydrateLoadableVideos();
        },
        slideChangeTransitionEnd: () => this._onActiveSlideChange()
      }
    });

    this._observeVisibility();
  }

  destroy() {
    this._stopSwiperAutoplay();
    this._detachVideoEndedHandler();
    this._pauseAllVideos();
    this._unbindMuteButtons();
    this._unbindPlayControls();

    if (this._visibilityObserver) {
      this._visibilityObserver.disconnect();
      this._visibilityObserver = null;
    }

    if (!this.swiper) return;

    this.swiper.destroy(true, true);
    this.swiper = null;
  }

  _observeVisibility() {
    if (this._visibilityObserver) {
      this._visibilityObserver.disconnect();
    }

    if (!('IntersectionObserver' in window)) {
      this._isVisible = true;
      this._onActiveSlideChange();
      return;
    }

    this._visibilityObserver = new IntersectionObserver(
      (entries) => {
        const isVisible = entries.some((entry) => entry.isIntersecting);
        this._isVisible = isVisible;
        if (isVisible) {
          this._onActiveSlideChange();
        } else {
          this._stopSwiperAutoplay();
          this._pauseAllVideos();
        }
      },
      { threshold: 0.35 }
    );

    this._visibilityObserver.observe(this);
  }

  _getActiveSlide() {
    if (!this.swiper?.slides?.length) return null;
    return this.swiper.slides[this.swiper.activeIndex] || null;
  }

  _getSlideRealIndex(slide) {
    if (!slide) return null;

    const loopIndex = slide.getAttribute('data-swiper-slide-index');
    if (loopIndex !== null && loopIndex !== '') {
      return Number(loopIndex);
    }

    return Array.from(slide.parentElement?.children || []).indexOf(slide);
  }

  _isActiveSlide(slide) {
    if (!slide || !this.swiper) return false;

    const activeSlide = this._getActiveSlide();
    if (activeSlide && slide === activeSlide) return true;

    const slideIndex = this._getSlideRealIndex(slide);
    if (slideIndex === null || Number.isNaN(slideIndex)) {
      return slide.classList.contains('swiper-slide-active');
    }

    return slideIndex === this.swiper.realIndex;
  }

  _getActiveVideo() {
    return this._getActiveSlide()?.querySelector('.ugc-meta-swiper__video') || null;
  }

  _getVideos(scope = this) {
    return Array.from(scope.querySelectorAll('.ugc-meta-swiper__video'));
  }

  _getLoadableSlides() {
    if (!this._isVisible) return [];

    return Array.from(
      this.querySelectorAll(
        '.swiper-slide-active, .swiper-slide-prev, .swiper-slide-next'
      )
    );
  }

  _hydrateVideo(video) {
    if (!video || video.dataset.ugcLoaded === 'true') return;

    video.querySelectorAll('source[data-src]').forEach((source) => {
      source.src = source.dataset.src;
    });

    video.preload = 'metadata';
    video.dataset.ugcLoaded = 'true';
    video.load();
  }

  _hydrateLoadableVideos() {
    this._getLoadableSlides().forEach((slide) => {
      this._getVideos(slide).forEach((video) => this._hydrateVideo(video));
    });
  }

  _enforceCustomVideoControls(video) {
    if (!video) return;
    video.controls = false;
    video.removeAttribute('controls');
  }

  _pauseAllVideos() {
    this._getVideos().forEach((video) => {
      video.pause();
    });
  }

  _stopSwiperAutoplay() {
    this.swiper?.autoplay?.stop();
  }

  _startSwiperAutoplay() {
    if (!this._shouldAutoplay || !this.swiper?.autoplay) return;
    this.swiper.autoplay.start();
  }

  _detachVideoEndedHandler(video = null) {
    const videos = video ? [video] : this._getVideos();

    videos.forEach((el) => {
      if (el._ugcEndedHandler) {
        el.removeEventListener('ended', el._ugcEndedHandler);
        el._ugcEndedHandler = null;
      }
      if (el._ugcDurationFallback) {
        clearTimeout(el._ugcDurationFallback);
        el._ugcDurationFallback = null;
      }
      el._ugcAdvanceHandled = false;
    });
  }

  _onActiveSlideChange() {
    if (!this._isVisible) return;

    this._detachVideoEndedHandler();
    this._hydrateLoadableVideos();
    this._syncVideos();
    this._syncSwiperAutoplay();
  }

  _syncVideos() {
    const activeSlide = this._getActiveSlide();
    if (!activeSlide) return;

    this._getVideos().forEach((video) => {
      this._enforceCustomVideoControls(video);
      const slide = video.closest('.swiper-slide');
      const isActive = this._isActiveSlide(slide);

      video.loop = !this._shouldAutoplay && isActive;

      if (!isActive) {
        this._detachVideoEndedHandler(video);
        video.pause();
        video.currentTime = 0;
        const inactiveMedia = video.closest('.ugc-meta-swiper__media--video');
        if (inactiveMedia) {
          this._setVideoProgress(inactiveMedia, 0);
          this._updatePlayUi(inactiveMedia, video);
        }
        return;
      }

      this._hydrateVideo(video);
      video.currentTime = 0;
      const media = video.closest('.ugc-meta-swiper__media--video');
      const playPromise = video.play();

      if (media) {
        this._setVideoProgress(media, 0);
        if (playPromise && typeof playPromise.then === 'function') {
          playPromise
            .then(() => this._updatePlayUi(media, video))
            .catch(() => this._updatePlayUi(media, video));
        } else {
          this._updatePlayUi(media, video);
        }
      } else if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    });
  }

  _syncSwiperAutoplay() {
    if (!this.swiper || !this._shouldAutoplay) return;

    const activeSlide = this._getActiveSlide();
    if (!activeSlide) return;

    const video = this._getActiveVideo();
    const slideType = activeSlide.dataset.ugcSlideType;

    if (video || slideType === 'video') {
      this._stopSwiperAutoplay();
      this._bindVideoEndedAdvance(video);
      return;
    }

    this._startSwiperAutoplay();
  }

  _isActiveVideo(video) {
    return this._getActiveVideo() === video;
  }

  _bindVideoEndedAdvance(video) {
    if (!video || !this._shouldAutoplay) return;

    this._hydrateVideo(video);
    this._detachVideoEndedHandler(video);
    video.loop = false;

    const onComplete = () => {
      if (!this._isActiveVideo(video) || video._ugcAdvanceHandled) return;
      video._ugcAdvanceHandled = true;
      this._detachVideoEndedHandler(video);
      this._advanceSwiper();
    };

    const attachEndedListener = () => {
      if (video._ugcEndedHandler) return;

      video._ugcEndedHandler = onComplete;
      video.addEventListener('ended', onComplete, { once: true });
      this._armVideoDurationFallback(video, onComplete);
    };

    if (video.paused) {
      const playPromise = video.play();
      if (playPromise && typeof playPromise.then === 'function') {
        playPromise.then(attachEndedListener).catch(() => {
          this._startSwiperAutoplay();
        });
      } else {
        attachEndedListener();
      }
    } else {
      attachEndedListener();
    }
  }

  _armVideoDurationFallback(video, onComplete) {
    const arm = () => {
      if (!video.duration || !Number.isFinite(video.duration)) {
        return;
      }

      const remainingMs = Math.max(
        (video.duration - video.currentTime) * 1000 + 400,
        500
      );

      video._ugcDurationFallback = setTimeout(() => {
        if (!this._isActiveVideo(video)) return;
        if (video.ended || video.currentTime >= video.duration - 0.2) {
          onComplete();
        }
      }, remainingMs);
    };

    if (video.readyState >= 1) {
      arm();
    } else {
      video.addEventListener('loadedmetadata', arm, { once: true });
    }
  }

  _advanceSwiper() {
    if (!this.swiper) return;

    if (this.swiper.isEnd && !this.swiper.params.loop) {
      this.swiper.slideTo(0);
    } else {
      this.swiper.slideNext();
    }
  }

  _bindPlayControls() {
    this._unbindPlayControls();

    this.querySelectorAll('.ugc-meta-swiper__media--video').forEach((media) => {
      const video = media.querySelector('.ugc-meta-swiper__video');
      const playButton = media.querySelector('[data-ugc-play]');
      if (!video) return;
      this._enforceCustomVideoControls(video);

      const onMediaClick = (event) => {
        if (event.target.closest('[data-ugc-mute]') || event.target.closest('[data-ugc-play]')) {
          return;
        }
        this._toggleVideoPlayback(media, video);
      };

      const onPlayButtonClick = (event) => {
        event.stopPropagation();
        this._toggleVideoPlayback(media, video);
      };

      const onPlay = () => {
        this._updatePlayUi(media, video);
        this._updateVideoProgress(media, video);
      };
      const onPause = () => {
        this._updatePlayUi(media, video);
        this._updateVideoProgress(media, video);
      };
      const onTimeUpdate = () => this._updateVideoProgress(media, video);
      const onMetadata = () => this._updateVideoProgress(media, video);
      const onEnded = () => this._setVideoProgress(media, 1);

      media._ugcMediaClickHandler = onMediaClick;
      playButton._ugcPlayClickHandler = onPlayButtonClick;
      video._ugcPlayHandler = onPlay;
      video._ugcPauseHandler = onPause;
      video._ugcTimeUpdateHandler = onTimeUpdate;
      video._ugcMetadataHandler = onMetadata;
      video._ugcEndedUiHandler = onEnded;

      media.addEventListener('click', onMediaClick);
      playButton?.addEventListener('click', onPlayButtonClick);
      video.addEventListener('play', onPlay);
      video.addEventListener('pause', onPause);
      video.addEventListener('timeupdate', onTimeUpdate);
      video.addEventListener('loadedmetadata', onMetadata);
      video.addEventListener('durationchange', onMetadata);
      video.addEventListener('ended', onEnded);

      this._updatePlayUi(media, video);
      this._updateVideoProgress(media, video);
    });
  }

  _unbindPlayControls() {
    this.querySelectorAll('.ugc-meta-swiper__media--video').forEach((media) => {
      const video = media.querySelector('.ugc-meta-swiper__video');
      const playButton = media.querySelector('[data-ugc-play]');

      if (media._ugcMediaClickHandler) {
        media.removeEventListener('click', media._ugcMediaClickHandler);
        media._ugcMediaClickHandler = null;
      }

      if (playButton?._ugcPlayClickHandler) {
        playButton.removeEventListener('click', playButton._ugcPlayClickHandler);
        playButton._ugcPlayClickHandler = null;
      }

      if (video?._ugcPlayHandler) {
        video.removeEventListener('play', video._ugcPlayHandler);
        video.removeEventListener('pause', video._ugcPauseHandler);
        video.removeEventListener('timeupdate', video._ugcTimeUpdateHandler);
        video.removeEventListener('loadedmetadata', video._ugcMetadataHandler);
        video.removeEventListener('durationchange', video._ugcMetadataHandler);
        video.removeEventListener('ended', video._ugcEndedUiHandler);
        video._ugcPlayHandler = null;
        video._ugcPauseHandler = null;
        video._ugcTimeUpdateHandler = null;
        video._ugcMetadataHandler = null;
        video._ugcEndedUiHandler = null;
      }

      this._clearControlsHideTimer(media);
    });
  }

  _clearControlsHideTimer(media) {
    if (media._controlsHideTimer) {
      clearTimeout(media._controlsHideTimer);
      media._controlsHideTimer = null;
    }
  }

  _setVideoProgress(media, progress) {
    const progressFill = media?.querySelector('[data-ugc-video-progress-fill]');
    if (!progressFill) return;

    const clampedProgress = Math.min(Math.max(progress, 0), 1);
    progressFill.style.transform = `scaleX(${clampedProgress})`;
  }

  _updateVideoProgress(media, video) {
    if (!media || !video) return;
    if (video.ended) {
      this._setVideoProgress(media, 1);
      return;
    }

    if (!video.duration || !Number.isFinite(video.duration)) {
      this._setVideoProgress(media, 0);
      return;
    }

    this._setVideoProgress(media, video.currentTime / video.duration);
  }

  _revealPlayControl(media) {
    media.classList.add('is-controls-visible');
    this._clearControlsHideTimer(media);

    const video = media.querySelector('.ugc-meta-swiper__video');
    if (!video || video.paused) return;

    media._controlsHideTimer = setTimeout(() => {
      media.classList.remove('is-controls-visible');
    }, this._controlsHideDelay);
  }

  _updatePlayUi(media, video) {
    const isPaused = video.paused;
    const isActive = this._isActiveSlide(media.closest('.swiper-slide'));

    media.classList.toggle('is-paused', isPaused && isActive);
    media.classList.toggle('is-playing', !isPaused && isActive);

    if (!isActive) {
      media.classList.remove('is-controls-visible');
      this._clearControlsHideTimer(media);
    } else if (isPaused) {
      media.classList.add('is-controls-visible');
      this._clearControlsHideTimer(media);
    }

    const playButton = media.querySelector('[data-ugc-play]');
    if (!playButton) return;

    const playIcon = playButton.querySelector('.ugc-meta-swiper__play-icon--play');
    const pauseIcon = playButton.querySelector('.ugc-meta-swiper__play-icon--pause');

    playIcon?.classList.toggle('visually-hidden', !isPaused);
    pauseIcon?.classList.toggle('visually-hidden', isPaused);
    playButton.setAttribute(
      'aria-label',
      isPaused ? 'Play video' : 'Pause video'
    );
  }

  _toggleVideoPlayback(media, video) {
    const slide = media.closest('.swiper-slide');
    if (!this._isActiveSlide(slide)) return;

    if (video.paused) {
      this._hydrateVideo(video);
      video.loop = !this._shouldAutoplay;
      const playPromise = video.play();

      if (this._shouldAutoplay) {
        this._stopSwiperAutoplay();
        if (playPromise && typeof playPromise.then === 'function') {
          playPromise
            .then(() => {
              this._bindVideoEndedAdvance(video);
              this._revealPlayControl(media);
              this._updatePlayUi(media, video);
            })
            .catch(() => this._updatePlayUi(media, video));
          return;
        }

        this._bindVideoEndedAdvance(video);
      } else if (playPromise && typeof playPromise.then === 'function') {
        playPromise
          .then(() => {
            this._revealPlayControl(media);
            this._updatePlayUi(media, video);
          })
          .catch(() => this._updatePlayUi(media, video));
        return;
      }
    } else {
      video.pause();
      this._detachVideoEndedHandler(video);
      this._stopSwiperAutoplay();
    }

    this._revealPlayControl(media);
    this._updatePlayUi(media, video);
  }

  _bindMuteButtons() {
    this._unbindMuteButtons();
    this.querySelectorAll('[data-ugc-mute]').forEach((button) => {
      button.addEventListener('click', this._muteHandler);
    });
  }

  _unbindMuteButtons() {
    this.querySelectorAll('[data-ugc-mute]').forEach((button) => {
      button.removeEventListener('click', this._muteHandler);
    });
  }

  _onMuteClick(event) {
    event.stopPropagation();
    const button = event.currentTarget;
    const slide = button.closest('.swiper-slide');
    const video = slide?.querySelector('.ugc-meta-swiper__video');
    if (!video) return;

    this._hydrateVideo(video);
    video.muted = !video.muted;
    this._updateMuteButton(button, video);

    if (!video.muted && this._isActiveSlide(slide)) {
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    }
  }

  _updateMuteButton(button, video) {
    const iconOff = button.querySelector('.ugc-meta-swiper__mute-icon--off');
    const iconOn = button.querySelector('.ugc-meta-swiper__mute-icon--on');
    const isMuted = video.muted;

    button.setAttribute('aria-pressed', isMuted ? 'false' : 'true');
    button.setAttribute(
      'aria-label',
      isMuted ? 'Unmute video' : 'Mute video'
    );

    iconOff?.classList.toggle('visually-hidden', !isMuted);
    iconOn?.classList.toggle('visually-hidden', isMuted);
  }
}

customElements.define('ugc-meta-swiper', UgcMetaSwiper);
})();
